export enum FontStyle {
    BOLD = 'BOLD',
    ITALIC = 'ITALIC',
    BOLD_ITALIC = 'BOLD_ITALIC',
    REGULAR = 'REGULAR',
}

export type FontId = number | string;

const BOLD_WEIGHT_THRESHOLD = 600;
const ITALIC_STYLES = ['ITALIC', 'BOLD_ITALIC', 'LIGHT_IT'];
const BOLD_STYLES = ['BOLD', 'BOLD_ITALIC', 'GRAS'];
const LIGHT_STYLES = ['LIGHT', 'LIGHT_IT'];

export type FontStyleSource = {
    style: string | null;
    tag?: string | null;
    weight?: string | number | null;
};

const hasStyle = (styles: string[], style: FontStyleSource['style']) => !!style && styles.includes(style);

export const deriveFontStyle = ({ style, tag, weight }: FontStyleSource): FontStyle => {
    const canonical = Object.values(FontStyle).find((value) => value === style);

    if (canonical) {
        return canonical;
    }

    const isItalic = hasStyle(ITALIC_STYLES, style) || tag === 'ITALIC' || tag === 'OBLIQUE';
    const isBold =
        hasStyle(BOLD_STYLES, style) || (!hasStyle(LIGHT_STYLES, style) && Number(weight) >= BOLD_WEIGHT_THRESHOLD);

    if (isBold && isItalic) {
        return FontStyle.BOLD_ITALIC;
    }

    if (isBold) {
        return FontStyle.BOLD;
    }

    if (isItalic) {
        return FontStyle.ITALIC;
    }

    return FontStyle.REGULAR;
};

export type Font = {
    id: FontId;
    name: string;
    tag: string;
    url: string;
    weight: string;
    style: FontStyle;
    includeLineGap?: boolean;
    /** Every id the font answers to, `id` included. Single-entry unless mappings were repointed. */
    referenceIds: FontId[];
    isActive: boolean;
};

export type FontFamily = {
    fonts: Font[];
    name: string;
};

let fontFamilies: FontFamily[] = [];

export const setFontFamilies = (data: FontFamily[]) => {
    fontFamilies = data;
};

export const getFontFamilies = (): Readonly<FontFamily[]> => {
    if (!fontFamilies) {
        throw new Error('Font families are not set');
    }

    return fontFamilies;
};

// A design persists any one of a font's reference ids, not necessarily `id` (= `referenceIds[0]`),
// so every lookup has to accept them all. `id` is still compared first so that a family which
// reaches here without `referenceIds` degrades to the old behaviour instead of throwing.
export const fontMatchesId = (font: Font, fontId: FontId): boolean => {
    if (fontId === null || fontId === undefined) {
        return false;
    }

    const target = String(fontId);

    return String(font.id) === target || (font.referenceIds ?? []).some((id) => String(id) === target);
};

export const findFontById = (families: Readonly<FontFamily[]>, fontId: FontId) =>
    families.flatMap((family) => family.fonts).find((font) => fontMatchesId(font, fontId));

export const getFontById = (id: FontId) => findFontById(fontFamilies, id);
